import { graphql, useStaticQuery } from 'gatsby';
import { uniq } from 'lodash-es';

import type { MoneyV2 } from '@shopify/hydrogen-react/storefront-api-types';

import { useClientOnlyCart as useCart } from '../shopify/hooks/useClientOnlyCart';
import { useMemo } from 'react';
import type { ExtendedCart } from '../../pages/cart';
import useLoggedInCustomer from './useLoggedInCustomer';

export enum AutomaticDiscountType {
  Basic = 'ShopifyAdmin_DiscountAutomaticBasic',
  FreeShipping = 'ShopifyAdmin_DiscountAutomaticFreeShipping'
}

export enum AutomaticDiscountMinimumRequirementType {
  Quantity = 'ShopifyAdmin_DiscountMinimumQuantity',
  Subtotal = 'ShopifyAdmin_DiscountMinimumSubtotal'
}

export interface AutomaticDiscount {
  title: string
  type: AutomaticDiscountType
  minimumRequirement: AutomaticDiscountMinimumRequirement
  status: AutomaticDiscountStatus
  startsAt?: string
  endsAt?: string
  customerGets?: Queries.ShopifyAdmin_DiscountAutomaticBasic['customerGets']
}

export interface AutomaticDiscountMinimumRequirement {
  type: AutomaticDiscountMinimumRequirementType
  greaterThanOrEqualToQuantity?: string
  greaterThanOrEqualToSubtotal?: MoneyV2
}

export enum AutomaticDiscountStatus {
  Active = 'ACTIVE',
  Scheduled = 'SCHEDULED',
  Expired = 'EXPIRED'
}

const getWholesaleDiscountPercent = (quantity: number): number | null => {
  if (quantity >= 10 && quantity <= 30) return 40;
  if (quantity >= 31 && quantity <= 50) return 45;
  if (quantity >= 51 && quantity <= 100) return 50;
  if (quantity >= 101) return 55;
  return null;
}

export const useAutomaticDiscountList = () => {
  const discountQuery: Queries.AutomaticDiscountsQuery = useStaticQuery(graphql`
        query AutomaticDiscounts {
            shopifyAdmin {
                automaticDiscountNodes(first: 10, sortKey: CREATED_AT) {
                  nodes {
                    automaticDiscount {
                      type: __typename
                      ... on ShopifyAdmin_DiscountAutomaticFreeShipping {
                        title
                        status
                        startsAt
                        endsAt
                        minimumRequirement {
                          type: __typename
                          ... on ShopifyAdmin_DiscountMinimumQuantity {
                            greaterThanOrEqualToQuantity
                          }
                          ... on ShopifyAdmin_DiscountMinimumSubtotal {
                            greaterThanOrEqualToSubtotal {
                              amount
                              currencyCode
                            }
                          }
                        }
                      }
                      ... on ShopifyAdmin_DiscountAutomaticBasic {
                        title
                        status
                        startsAt
                        endsAt
                        customerGets {
                          items {
                            ... on ShopifyAdmin_AllDiscountItems {
                              allItems
                              
                            }
                          }
                          value {
                            ...on ShopifyAdmin_DiscountPercentage {
                              percentage                              
                            }
                          }
                        }                        
                        minimumRequirement {
                          type: __typename
                          ... on ShopifyAdmin_DiscountMinimumQuantity {
                            greaterThanOrEqualToQuantity
                          }
                          ... on ShopifyAdmin_DiscountMinimumSubtotal {
                            greaterThanOrEqualToSubtotal {
                              amount
                              currencyCode
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }           
        }
        `)

  const { cost, discountAllocations, totalQuantity } = useCart() as ExtendedCart;

  const { wholesale } = useLoggedInCustomer();

  const allAutomaticDiscounts = useMemo(() => {
    return ((discountQuery?.shopifyAdmin?.automaticDiscountNodes?.nodes || []).map(e => e.automaticDiscount) as AutomaticDiscount[]).filter(e => e.status === AutomaticDiscountStatus.Active).sort((a, b) => b.type.localeCompare(a.type));
  }, [cost, discountAllocations])

  const automaticDiscounts = !cost ? [] : allAutomaticDiscounts.filter(e => {
    if (e.type === AutomaticDiscountType.Basic) return true;

    if (e.type === AutomaticDiscountType.FreeShipping && Number(e.minimumRequirement.greaterThanOrEqualToSubtotal?.amount) > 0) {
      const minSubtotal = Number(e.minimumRequirement.greaterThanOrEqualToSubtotal?.amount || 0)
      const currentCartSubtotal = (Number(cost.subtotalAmount.amount) || 0);
      if (minSubtotal > 0 && currentCartSubtotal <= minSubtotal) {
        return true
      }
    }
    return false;
  })

  // console.log('heree allAutomaticDiscounts', allAutomaticDiscounts)

  const appliedDiscounts = !cost ? [] : (allAutomaticDiscounts || []).map((e) => {
    if (e.type === AutomaticDiscountType.Basic && discountAllocations?.find((d) => (d as any).title as string === e.title)) {
      return e;
    };

    if (e.type === AutomaticDiscountType.FreeShipping && Number(e.minimumRequirement.greaterThanOrEqualToSubtotal?.amount) > 0) {
      const minSubtotal = Number(e.minimumRequirement.greaterThanOrEqualToSubtotal?.amount || 0)
      const currentCartSubtotal = (Number(cost.subtotalAmount.amount) || 0);
      if (minSubtotal > 0 && currentCartSubtotal >= minSubtotal) {
        return e
      }
    }

    if (e.type === AutomaticDiscountType.FreeShipping && Number(e.minimumRequirement.greaterThanOrEqualToQuantity) > 0) {
      const minQty = Number(e.minimumRequirement.greaterThanOrEqualToQuantity || 0)
      if (minQty > 0 && totalQuantity >= minQty) {
        return e
      }
    }

    return null;
  }).filter(e => Boolean(e))

  const appliedDiscountTitles = uniq(appliedDiscounts.map(e => e.title)) as string[]
  const notYetAppliedDiscounts = (automaticDiscounts || []).filter(e => !appliedDiscountTitles.includes(e.title)).filter(e => {
    if (!appliedDiscounts || appliedDiscounts.length === 0) return true;
    if (e.customerGets && e.customerGets.value && (e.customerGets.value as Queries.ShopifyAdmin_DiscountPercentage).percentage > 0) {
      // If appliedDiscounts contains a discount with a higher percentage, don't show this discount
      const thisPercentage = (e.customerGets.value as Queries.ShopifyAdmin_DiscountPercentage).percentage;
      if (appliedDiscounts.find(d => (d.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage)?.percentage && (d.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage)?.percentage > thisPercentage)) {
        return false;
      }
    }
    return true
  })

  const defaultDiscounts = allAutomaticDiscounts.filter(e => e.status === 'ACTIVE' && e.type === "ShopifyAdmin_DiscountAutomaticBasic" && (e.minimumRequirement == null || parseInt(e.minimumRequirement.greaterThanOrEqualToQuantity) === 1));

  const getQuantityDiscounts = (quantity: number) => allAutomaticDiscounts.filter(d => d.status === 'ACTIVE' && d.type === "ShopifyAdmin_DiscountAutomaticBasic" && d.minimumRequirement && quantity >= parseInt(d.minimumRequirement.greaterThanOrEqualToQuantity));
  const getDefaultQuantityDiscount = (quantity: number) => {
    const quantityDiscounts = getQuantityDiscounts(quantity)
    return quantityDiscounts.length > 0 ? quantityDiscounts.sort(e => (e.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage).percentage).slice(0, 1)[0] : null
  }

  const defaultDiscount = defaultDiscounts.sort(e => (e.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage).percentage).slice(0, 1)[0];

  const discountForQuantity = (quantity: number) => {
    if (wholesale) {
      const wholesaleDisc = getWholesaleDiscountPercent(quantity);
      return {
        percentage: wholesaleDisc ? (wholesaleDisc / 100) : null
      }
    }

    const defaultQuantityDiscount = getDefaultQuantityDiscount(quantity)
    const defaultValue = defaultDiscount?.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage;
    const defaultQuantityValue = defaultQuantityDiscount?.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage;

    if (defaultQuantityValue && defaultValue) {
      return defaultQuantityValue.percentage > defaultValue.percentage ? defaultQuantityValue : defaultValue;
    } else if (defaultQuantityValue) {
      return defaultQuantityValue;
    }
    else if (defaultValue) {
      return defaultValue;
    }
    else {
      return null;
    }
  }

  //    const getProductPricingWithDiscount = (selectedVariant: AddToCartCardProps['product']['variants'][0], wholesale: boolean, quantity: number): ProductPriceWithDiscount => {
  //     const standardUnitPrice = selectedVariant.presentmentPrices[0].price.amount
  //     const discountPercent = wholesale ? getWholesaleDiscountPercent(quantity) : getStandardDiscountPercent(quantity)
  //     const discountedUnitPrice = (standardUnitPrice - (((discountPercent || 0) / 100) * standardUnitPrice));

  //     if(!wholesale){
  //         return {
  //         standardUnitPrice,
  //         discountPercent,
  //         discountedUnitPrice,
  //         quantity,
  //         currencyCode: selectedVariant.presentmentPrices[0].price.currencyCode as CurrencyCode
  //     }
  //     }

  //     return {
  //         standardUnitPrice,
  //         discountPercent,
  //         discountedUnitPrice,
  //         quantity,
  //         currencyCode: selectedVariant.presentmentPrices[0].price.currencyCode as CurrencyCode
  //     }
  // }

  // console.log('heree applied', appliedDiscounts)

  return {
    appliedDiscounts: appliedDiscounts,
    notAppliedDiscounts: notYetAppliedDiscounts,
    activeDiscounts: allAutomaticDiscounts,
    hasFreeShipping: Boolean(appliedDiscounts.find(e => e.type === AutomaticDiscountType.FreeShipping)),
    quantityDiscounts: getQuantityDiscounts,
    defaultDiscounts,
    defaultQuantityDiscount: getDefaultQuantityDiscount,
    defaultDiscount,
    isBigDefaultDiscount: defaultDiscount && (defaultDiscount.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage).percentage >= 0.25,
    defaultDiscountPercent: defaultDiscount && (defaultDiscount.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage).percentage >= 0.25 ? (defaultDiscount.customerGets?.value as Queries.ShopifyAdmin_DiscountPercentage).percentage * 100 : null,
    discountForQuantity,
    discountPriceForPrice: (itemQuantity: number, originalPrice: MoneyV2 | Queries.ShopifyMoneyV2, totalCartQuantity: number): MoneyV2 => {
      const theDiscount = discountForQuantity(totalCartQuantity);
      const discountPercent = (theDiscount?.percentage * 100) || 0;

      // console.log('heree', { itemQuantity, totalCartQuantity, originalPrice, discountPercent })

      return {
        ...originalPrice,
        amount: (discountPercent ? (parseFloat(originalPrice.amount.toString()) * (1 - discountPercent / 100)) : parseFloat(originalPrice.amount.toString())).toLocaleString()
      }
    }
  }
}